import type { CodeableConcept } from "./common";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { ObservationId } from "./observation.model";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";
import type { VisitId } from "./visit.model";

export type ConditionId = DomainId<"ConditionId">;

export function asConditionId(value: string): ConditionId {
  if (!value) throw new PreconditionFailedError("A condition identity cannot be empty");
  return value as ConditionId;
}

// ============================================================
// Condition — a diagnosis on this patient's problem list (ADR-0017)
//
// Evidence is the interesting part: symptoms observed over time are attached
// here as manifestations, so the diagnosis carries what supports it rather than
// standing on its own. Attaching is idempotent per Observation — the same
// symptom witnessed twice is one piece of evidence, not two.
//
// `assertVerificationValid` from the old model does NOT appear here. Judging a
// whole problem list is a policy over a SET, and a set is not an aggregate;
// that fold belongs to the caller, over `isClinicallyValid` below.
// ============================================================

type ClinicalStatus = "active" | "recurrence" | "relapse" | "inactive" | "remission" | "resolved";

type VerificationStatus = "unconfirmed" | "provisional" | "differential" | "confirmed" | "refuted" | "entered-in-error";

interface Evidence {
  /** The manifestation, optionally dual-coded (SNOMED + ICD-10) for one finding. */
  code?: CodeableConcept[];
  /** Observation ids — point-in-time captures of that manifestation. */
  observationIds?: ObservationId[];
}

/** Statuses that say the diagnosis is not believed. */
const NOT_BELIEVED: ReadonlySet<VerificationStatus> = new Set(["refuted", "entered-in-error"]);

/** Statuses that say the patient no longer has it. */
const NOT_LIVE: ReadonlySet<ClinicalStatus> = new Set(["resolved", "inactive"]);

export interface ConditionProps {
  id: ConditionId;
  clinicalStatus: ClinicalStatus;
  verificationStatus: VerificationStatus;
  category?: "encounter-diagnosis" | "problem-list-item";
  code: CodeableConcept;
  patientId: PatientId;
  encounterId?: VisitId;
  recorderId?: PractitionerId;
  recordedDate?: string;
  /** When the patient says it began. */
  onsetDateTime?: string;
  severity?: CodeableConcept;
  note?: string;
  evidence?: Evidence[];
}

/** Stored rows may omit statuses; restoration applies the established domain policy. */
export type StoredConditionProps = Omit<ConditionProps, "clinicalStatus" | "verificationStatus"> & {
  clinicalStatus?: ClinicalStatus;
  verificationStatus?: VerificationStatus;
};

export class Condition {
  private state: ConditionProps;

  private constructor(props: ConditionProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Condition requires a patient");
    }
    if (!props.code.coding?.length && !props.code.text) {
      throw new PreconditionFailedError("Condition requires a code");
    }
    this.state = structuredClone(props);
  }

  static create(input: ConditionProps): Condition {
    return new Condition({ ...structuredClone(input), id: asConditionId(input.id) });
  }

  static restore(snapshot: StoredConditionProps): Condition {
    return new Condition({
      ...snapshot,
      clinicalStatus: snapshot.clinicalStatus ?? "active",
      verificationStatus: snapshot.verificationStatus ?? "unconfirmed",
    });
  }

  // ── facts ──

  get id(): ConditionId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get code(): CodeableConcept {
    return structuredClone(this.state.code);
  }

  /** Believed to be a real diagnosis. What a stage gate folds over a problem list. */
  get isClinicallyValid(): boolean {
    return !NOT_BELIEVED.has(this.state.verificationStatus);
  }

  /** The patient has it now — live and believed. */
  get isLive(): boolean {
    return this.isClinicallyValid && !NOT_LIVE.has(this.state.clinicalStatus);
  }

  /** Observation ids already attached, across every manifestation. */
  get evidenceObservationIds(): readonly ObservationId[] {
    return (this.state.evidence ?? []).flatMap((e) => e.observationIds ?? []);
  }

  snapshot(): ConditionProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  /**
   * Attach a witnessed manifestation. Idempotent per Observation, so a symptom
   * re-submitted (a retry, a double-click) does not accumulate.
   */
  attachEvidence(code: CodeableConcept, observationId: ObservationId): void {
    if (NOT_LIVE.has(this.state.clinicalStatus)) {
      throw new PreconditionFailedError(
        `Cannot attach evidence to a condition in clinicalStatus '${this.state.clinicalStatus}'`,
      );
    }
    if (!this.isClinicallyValid) {
      throw new PreconditionFailedError(
        `Cannot attach evidence to a condition with verificationStatus '${this.state.verificationStatus}'`,
      );
    }
    if (this.evidenceObservationIds.includes(observationId)) return;
    this.state.evidence = [...(this.state.evidence ?? []), { code: [code], observationIds: [observationId] }];
  }

  /** Revise the clinician's reading of it. */
  revise(changes: {
    clinicalStatus?: ClinicalStatus;
    verificationStatus?: VerificationStatus;
    severity?: CodeableConcept;
    onsetDateTime?: string;
    note?: string;
  }): void {
    Object.assign(this.state, changes);
  }
}
